import { useEffect, useRef } from "react";
import { IntlProvider as ReactIntlProvider } from "react-intl";

import { type SupportedLocale, getMessages, resolveLocale } from "@/i18n";
import { useSession } from "@/lib/auth-client";
import { trpc } from "@/lib/trpc";
import { useLocaleStore } from "@/stores/locale";

export function IntlProvider({ children }: { children: React.ReactNode }) {
  const { data: sessionData } = useSession();
  const hasSession = !!sessionData?.session;

  // Server-owned locale for authenticated users
  const { data: settings, isSuccess: settingsLoaded } = trpc.settings.get.useQuery(undefined, {
    enabled: hasSession,
  });

  const utils = trpc.useUtils();
  const updateSettings = trpc.settings.update.useMutation({
    onSuccess: () => utils.settings.get.invalidate(),
  });

  // Guest locale from localStorage (Zustand persist)
  const guestLocale = useLocaleStore((s) => s.locale);

  // Migrate guest locale preference to server on first authenticated load.
  // Only runs when the server row is new (isDefault) and the guest picked
  // a non-default locale, preserving the choice they made before logging in.
  const hasMigrated = useRef(false);
  useEffect(() => {
    if (settingsLoaded && !hasMigrated.current && hasSession && settings) {
      hasMigrated.current = true;
      if (settings.isDefault && guestLocale !== "en") {
        updateSettings.mutate({ locale: guestLocale });
      }
    }
  }, [settingsLoaded, hasSession, settings?.isDefault, updateSettings, guestLocale, settings]);

  // Server settings take precedence for authenticated users
  const locale: SupportedLocale =
    hasSession && settings ? resolveLocale(settings.locale) : guestLocale;
  const messages = getMessages(locale);

  // Keep <html lang> in sync for accessibility & SEO
  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  return (
    <ReactIntlProvider locale={locale} defaultLocale="en" messages={messages}>
      {children}
    </ReactIntlProvider>
  );
}
